/**
 * Select
 */

import { memo, ReactNode } from 'react';

import { IconButton, MenuItem, Select as MuiSelect } from '@mui/material';

import { SelectType, SelectOptionType } from 'app/components/Select/types';

import ClearIcon from '@mui/icons-material/Clear';
import { dropdownDimensions } from '../JobLogConsultantQuoteSection/constants';
// defaultRenderValue
export const defaultRenderValue =
  (
    options: SelectType['options'],
    placeHolder?: ReactNode,
    renderValue?: SelectType['renderValue'] | undefined,
  ) =>
  (selected: string) => {
    if (!selected && placeHolder) return placeHolder;

    if (!!renderValue) {
      return renderValue(selected);
    }

    return options.find(option => option.value === selected)?.displayName;
  };

// MUI 9 no longer forwards a top-level aria-label to the Select root element -
// it is dropped silently. Route it through the root slot so call sites that
// label the whole control keep working, without clobbering the aria-label
// GenerateField puts on the inner combobox via inputProps.
const withRootAriaLabel = (
  ariaLabel: string | undefined,
  slotProps: Record<string, any> | undefined,
) =>
  ariaLabel === undefined
    ? slotProps
    : {
        ...slotProps,
        root: { 'aria-label': ariaLabel, ...slotProps?.root },
      };

export const Select = ({
  options,
  value,
  renderValue,
  defaultValue,
  MenuProps,
  placeholder,
  handleClear,
  disabled,
  'aria-label': ariaLabel,
  slotProps,
  ...props
}: SelectType) => {
  const placeHolder = (
    <p className="f-14 f-w-400 l-h-20 text-hint text-truncate">{placeholder}</p>
  );
  return (
    <MuiSelect
      className="overflow"
      displayEmpty
      value={value}
      defaultValue={defaultValue || ''}
      {...props}
      slotProps={withRootAriaLabel(ariaLabel, slotProps)}
      renderValue={defaultRenderValue(options, placeHolder, renderValue)}
      // this is to fix the size of the select
      // v9 renamed the *Props escape hatches to slotProps.
      MenuProps={{
        ...MenuProps,
        slotProps: { paper: { sx: dropdownDimensions } },
      }}
      endAdornment={
        value &&
        !disabled &&
        handleClear && (
          <IconButton
            onClick={() => {
              handleClear();
            }}
            size="small"
            className="select-clear-button"
          >
            <ClearIcon />
          </IconButton>
        )
      }
      disabled={disabled}
    >
      {options.map((item: SelectOptionType) => (
        <MenuItem
          key={item.key}
          aria-label={item.key}
          value={item.value}
          disabled={item.isDisabled}
        >
          {item.displayName}
        </MenuItem>
      ))}
    </MuiSelect>
  );
};

export default memo(Select);
